// src/careComponents/components/RoomsBedsMetaSection.tsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HotelRoundedIcon from "@mui/icons-material/HotelRounded";
import DoorFrontRoundedIcon from "@mui/icons-material/DoorFrontRounded";
import DoorSlidingRoundedIcon from "@mui/icons-material/DoorSlidingRounded";
import useBusinessFromStore from "../../../../platform/careComponents/hooks/useBusinessFromStore";

type Props = { title?: string; defaultExpanded?: boolean; panelMode?: boolean };

function StatChip({
  label,
  value,
  IconCmp,
}: {
  label: string;
  value?: number;
  IconCmp: React.ElementType;
}) {
  return (
    <Chip
      icon={<IconCmp />}
      label={`${label}: ${value ?? "—"}`}
      variant="outlined"
      sx={{
        fontWeight: 600,
        fontSize: { xs: "0.75rem", sm: "0.875rem" },
        height: { xs: 28, sm: 32 },
        "& .MuiChip-icon": { fontSize: { xs: "1rem", sm: "1.25rem" } },
      }}
    />
  );
}

type BedDetail = {
  id?: string | number;
  roomName?: string | null;
  roomType?: string | null;
  room?: string | null;
  bedLabel?: string | null;
  bedNumber?: string | number | null;
  gender?: string | null;
  isOccupied?: boolean | number | string | null;
  isOpen?: boolean | number | string | null;
  available?: boolean | number | string | null;
  occupied?: boolean | number | string | null;
  status?: string | null;
  [k: string]: any;
};

/** Robust bool casting for mixed backends: 1/"1"/"true"/"yes" => true */
function toBool(v: any): boolean {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (!s) return false;
    return ["1", "true", "t", "yes", "y"].includes(s);
  }
  return Boolean(v);
}

/** Decide if a bed is OPEN, handling different flag names/inversions */
function isBedOpen(b: BedDetail): boolean {
  // Most canonical: isOccupied => OPEN when false
  if ("isOccupied" in b) return !toBool(b.isOccupied);
  // Alternative flags (already in open semantics)
  if ("isOpen" in b) return toBool(b.isOpen);
  if ("available" in b) return toBool(b.available);
  // Inverted alternative
  if ("occupied" in b) return !toBool(b.occupied);
  // Status strings
  if ("status" in b && b.status != null) {
    const s = String(b.status).trim().toUpperCase();
    if (["OPEN", "VACANT", "FREE", "AVAILABLE"].includes(s)) return true;
    if (["OCCUPIED", "TAKEN", "CLOSED", "UNAVAILABLE"].includes(s)) return false;
  }
  // Default safe fallback
  return false;
}

export default function RoomsBedsMetaSection({
  title = "Rooms & Beds Availability",
  defaultExpanded = false,
  panelMode = false,
}: Props) {
  const { business } = useBusinessFromStore();
  const rawMeta = business?.careMeta;

  // Parse careMeta if it came as a string
  const meta: any = React.useMemo(() => {
    if (!rawMeta) return {};
    if (typeof rawMeta === "string") {
      try {
        return JSON.parse(rawMeta);
      } catch {
        return {};
      }
    }
    return rawMeta;
  }, [rawMeta]);

  const bedDetails: BedDetail[] = React.useMemo(
    () => (Array.isArray(meta?.bedDetails) ? meta.bedDetails : []),
    [meta]
  );

  // Prefer computing from details so flags can be normalized;
  // fall back to summary numbers if details are missing
  const computedOpen = bedDetails.filter(isBedOpen).length;
  const computedTotal = bedDetails.length;

  const bedsOpen: number =
    typeof meta?.bedsOpen === "number" && computedTotal === 0
      ? meta.bedsOpen
      : computedOpen;

  const originalBedsTotal = Number(meta?.bedsTotal ?? 0);
  const bedsTotal: number =
    originalBedsTotal > 0 ? originalBedsTotal : computedTotal;

  const roomsOpen: number | undefined =
    typeof meta?.roomsOpen === "number" ? meta.roomsOpen : undefined;

  const notes: string | null | undefined = meta?.notes ?? null;

  // Group by room (fallback to "Beds")
  const grouped = React.useMemo(() => {
    const map = new Map<string, BedDetail[]>();
    bedDetails.forEach((b) => {
      const key = (b.roomName ?? b.room ?? "").toString().trim() || "Beds";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(b);
    });
    return Array.from(map.entries()).map(([roomName, beds]) => {
      const open = beds.filter(isBedOpen).length;
      return { roomName, beds, open, total: beds.length };
    });
  }, [bedDetails]);

  const empty = bedsTotal === 0;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      expanded={panelMode ? true : undefined}
      onChange={panelMode ? () => { } : undefined}
      disableGutters={panelMode}
      sx={{
        mb: { xs: 1.5, sm: 2 },
        ...(panelMode
          ? { boxShadow: "none", border: 0, "&:before": { display: "none" } }
          : {}),
      }}
    >
      <AccordionSummary expandIcon={panelMode ? <></> : <ExpandMoreIcon />}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%", pr: 1 }}
          spacing={{ xs: 1, sm: 0 }}
        >
          <Typography variant="h6">{title}</Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
            <StatChip label="Beds Open" value={bedsOpen} IconCmp={HotelRoundedIcon} />
            <StatChip label="Beds Total" value={bedsTotal} IconCmp={HotelRoundedIcon} />
            <StatChip label="Rooms Open" value={roomsOpen} IconCmp={DoorFrontRoundedIcon} />
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={panelMode ? { px: 0, pb: 0, pt: 0 } : undefined}>
        {/* Clear read-only message to avoid confusion */}
        <Alert severity="info" sx={{ mb: 1.25 }}>
          This overview is read-only. To edit go to Care Settings
        </Alert>

        {notes ? (
          <Box sx={{ mb: 1.5, px: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {notes}
            </Typography>
          </Box>
        ) : null}

        {empty ? (
          <Typography variant="body2" color="text.secondary">
            No bed details found. To add or edit, go to the <strong>Care Settings</strong> section above.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {grouped.map((g) => (
              <Accordion key={g.roomName} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%", pr: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DoorSlidingRoundedIcon fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>
                        {g.roomName}
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={`Open ${g.open}/${g.total}`}
                      color={g.open > 0 ? "success" : "default"}
                      variant={g.open > 0 ? "filled" : "outlined"}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                    {g.beds.map((b, i) => {
                      const primary =
                        b.bedLabel ?? (b.bedNumber != null ? `#${b.bedNumber}` : `Bed ${i + 1}`);
                      const open = isBedOpen(b);
                      return (
                        <Grid key={`${g.roomName}-${primary}-${i}`} item xs={12} sm={6} md={4} lg={3}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: { xs: 1, sm: 1.5 },
                              borderRadius: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                                Bed {primary}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                {b.gender ? `Pref: ${b.gender}` : "Any Gender"}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={open ? "Open" : "Occupied"}
                              color={open ? "success" : "default"}
                              variant={open ? "filled" : "outlined"}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
